import { z } from 'zod'
import { Address, ChainId, Handle, IsoTimestamp, MicroString, Uuid } from '../primitives.js'
import { ApiKeyOwnerKind, PlanId, PlanSubscriptionStatus, UserStatus } from '../enums.js'

export const User = z.object({
  id: Uuid,
  handle: Handle.nullable(),
  /** The wallet the user signed up with (lower-case). */
  primaryAddress: Address,
  displayName: z.string(),
  avatarUrl: z.string().nullable(),
  status: UserStatus,
  createdAt: IsoTimestamp,
})
export type User = z.output<typeof User>

export const LinkedWallet = z.object({
  id: Uuid,
  chainId: ChainId,
  address: Address,
  verifiedAt: IsoTimestamp,
  isPrimary: z.boolean(),
  label: z.string().nullable(),
  createdAt: IsoTimestamp,
})
export type LinkedWallet = z.output<typeof LinkedWallet>

export const Me = z.object({
  user: User,
  plan: z.object({ planId: PlanId, status: PlanSubscriptionStatus, periodEnd: IsoTimestamp }).nullable(),
  credits: z.object({ availableMicro: MicroString, heldMicro: MicroString, settledMicro: MicroString }),
  wallets: z.array(LinkedWallet),
  counts: z.object({
    agents: z.number().int().nonnegative(),
    following: z.number().int().nonnegative(),
    /** Trading wallets (arbitrage custody). */
    wallets: z.number().int().nonnegative(),
    subscriptions: z.number().int().nonnegative(),
  }),
  /** The primary address is in `ALGOBOO_ADMIN_ADDRESSES` (shows `/admin`); absent from older producers. */
  isAdmin: z.boolean().optional(),
})
export type Me = z.output<typeof Me>

export const PatchMe = z
  .object({
    handle: Handle,
    displayName: z.string().min(1).max(60),
    avatarUrl: z.url().nullable(),
  })
  .partial()
export type PatchMe = z.input<typeof PatchMe>

// Sign-in with Ethereum (EIP-4361 on chain 4663) ------------------------------------

export const SiweNonceResult = z.object({
  nonce: z.string().min(8),
  issuedAt: IsoTimestamp,
  expiresAt: IsoTimestamp,
  /** The EIP-4361 `domain` the API verifies (ALGOBOO_SIWE_DOMAIN); clients should sign for exactly this. */
  domain: z.string().min(1).optional(),
  /** The chain id the API verifies (ALGOBOO_CHAIN_ID: 4663 live, 31337 anvil). */
  chainId: z.number().int().positive().optional(),
})
export type SiweNonceResult = z.output<typeof SiweNonceResult>
/** Body for POST /auth/siwe/nonce: optionally bind the nonce to an address. */
export const SiweNonceRequest = z.object({ address: Address.optional() })
export type SiweNonceRequest = z.input<typeof SiweNonceRequest>
export const SiweVerify = z.object({ message: z.string().min(1), signature: z.string().regex(/^0x[0-9a-fA-F]+$/) })
export type SiweVerify = z.input<typeof SiweVerify>

// API keys --------------------------------------------------------------------------

export const ApiKey = z.object({
  id: Uuid,
  ownerKind: ApiKeyOwnerKind,
  ownerId: Uuid,
  prefix: z.string(),
  label: z.string(),
  scopes: z.array(z.string()),
  createdAt: IsoTimestamp,
  expiresAt: IsoTimestamp.nullable(),
  revokedAt: IsoTimestamp.nullable(),
  lastUsedAt: IsoTimestamp.nullable(),
})
export type ApiKey = z.output<typeof ApiKey>

export const CreateApiKey = z.object({
  label: z.string().min(1).max(60),
  scopes: z.array(z.enum(['read', 'write'])).default(['read']),
  expiresAt: IsoTimestamp.optional(),
})
export type CreateApiKey = z.input<typeof CreateApiKey>

/** Returned once on creation; `secret` is never shown again. */
export const ApiKeyCreated = z.object({ key: ApiKey, secret: z.string() })
export type ApiKeyCreated = z.output<typeof ApiKeyCreated>
